import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { googleSheetsCache } from "../../stores/google-sheets-cache";

interface ChartFieldsModalProps {
  chartType: "line" | "bar" | "pie";
  data: any[];
  currentXKey?: string;
  currentYKey?: string;
  sourceComponentId?: string;
  allComponents?: any[];
  groupDuplicates?: boolean;
  limit?: number;
  sortOrder?: "asc" | "desc";
  onClose: () => void;
  onSave: (xKey: string, yKey: string, groupDuplicates: boolean, limit?: number, sortOrder?: "asc" | "desc") => void;
}

export default function ChartFieldsModal({
  chartType,
  data,
  currentXKey,
  currentYKey,
  sourceComponentId,
  allComponents = [],
  groupDuplicates = false,
  limit,
  sortOrder = "desc",
  onClose,
  onSave,
}: ChartFieldsModalProps) {
  const [xKey, setXKey] = useState(currentXKey || "");
  const [yKey, setYKey] = useState(currentYKey || "");
  const [shouldGroupDuplicates, setShouldGroupDuplicates] = useState(groupDuplicates);
  const [dataLimit, setDataLimit] = useState<number | undefined>(limit);
  const [dataSortOrder, setDataSortOrder] = useState<"asc" | "desc">(sortOrder);

  // Buscar dados do componente de origem se necessário
  const getActualData = () => {
    if (sourceComponentId && allComponents.length > 0) {
      const sourceComponent = allComponents.find((c) => c.id === sourceComponentId);
      
      if (!sourceComponent) return data;
      
      // Se o componente fonte usa Google Sheets, buscar do cache
      if (sourceComponent.config?.googleSheets?.enabled) {
        const cached = googleSheetsCache.get(
          sourceComponent.config.googleSheets.spreadsheetId,
          sourceComponent.config.googleSheets.range || 'Sheet1'
        );
        if (cached && cached.data.length > 0) {
          console.log('📊 ChartFieldsModal: Usando dados do cache do Google Sheets', cached.data.length, 'registros');
          return cached.data;
        }
      }
      
      // Se for kanban, extrair cards de todas as colunas
      if (sourceComponent?.type === 'kanban' && sourceComponent?.config?.columns) {
        return sourceComponent.config.columns.flatMap((column: any) => 
          (column.cards || []).map((card: any) => ({
            ...card,
            columnId: column.id,
            columnTitle: column.title,
          }))
        );
      } else if (sourceComponent?.config?.dataSource?.data) {
        return sourceComponent.config.dataSource.data;
      }
    }
    return data;
  };

  const actualData = getActualData();

  // Detectar campos disponíveis nos dados
  const availableFields =
    actualData && actualData.length > 0 ? Object.keys(actualData[0]) : [];

  // Função para verificar se um campo contém valores numéricos
  const isNumericField = (fieldName: string) => {
    if (!actualData || actualData.length === 0) return false;
    
    // Verificar uma amostra de até 20 registros
    const sampleSize = Math.min(20, actualData.length);
    let numericCount = 0;
    
    for (let i = 0; i < sampleSize; i++) {
      const value = actualData[i][fieldName];
      // Verificar se é número ou string que pode ser convertida em número
      if (value !== null && value !== undefined && value !== '') {
        const num = Number(value);
        if (!isNaN(num)) {
          numericCount++;
        }
      }
    }
    
    // Se 80% ou mais dos valores são numéricos, considerar o campo como numérico
    return (numericCount / sampleSize) >= 0.8;
  };

  // Filtrar campos numéricos para o eixo Y
  const numericFields = availableFields.filter(isNumericField);

  useEffect(() => {
    // Se não houver chaves selecionadas, tentar detectar automaticamente
    if (!currentXKey && !currentYKey && availableFields.length >= 2) {
      // Para gráficos de pizza, usar 'name' e 'value' se existirem
      if (chartType === "pie") {
        const nameField =
          availableFields.find((f) => f.toLowerCase().includes("name")) ||
          availableFields.find((f) => f.toLowerCase().includes("nome")) ||
          availableFields.find((f) => f.toLowerCase().includes("label")) ||
          availableFields[0];
        const valueField =
          numericFields.find((f) => f.toLowerCase().includes("value")) ||
          numericFields.find((f) => f.toLowerCase().includes("valor")) ||
          numericFields[0];
        setXKey(nameField);
        setYKey(valueField);
      } else {
        // Para line/bar, primeira coluna é X, segunda é Y (numérica)
        setXKey(availableFields[0]);
        setYKey(numericFields[0] || availableFields[1]);
      }
    }
  }, [chartType, currentXKey, currentYKey, availableFields]);

  const handleSave = () => {
    if (xKey && yKey) {
      onSave(xKey, yKey, shouldGroupDuplicates, dataLimit, dataSortOrder);
      onClose();
    }
  };

  const getPreviewData = () => {
    if (!xKey || !yKey || !actualData || actualData.length === 0) return [];
    
    let processedData = actualData;
    
    if (shouldGroupDuplicates) {
      // Agrupar por xKey e somar yKey
      const grouped = actualData.reduce((acc: any, item: any) => {
        if (!item) return acc;
        
        const key = String(item[xKey] || '');
        const value = Number(item[yKey]) || 0;
        
        if (key) {
          if (acc[key]) {
            acc[key] += value;
          } else {
            acc[key] = value;
          }
        }
        
        return acc;
      }, {});
      
      processedData = Object.entries(grouped).map(([key, value]) => ({
        [xKey]: key,
        [yKey]: value,
      }));
    }
    
    // Ordenar por yKey (valor)
    const sortedData = [...processedData].sort((a, b) => {
      const aValue = Number(a[yKey]) || 0;
      const bValue = Number(b[yKey]) || 0;
      return dataSortOrder === "desc" ? bValue - aValue : aValue - bValue;
    });
    
    // Aplicar limite se definido
    if (dataLimit && dataLimit > 0) {
      return sortedData.slice(0, dataLimit);
    }
    
    return sortedData;
  };

  const previewData = getPreviewData() || [];

  const getFieldLabel = () => {
    if (chartType === "pie") {
      return { x: "Campo de Rótulo", y: "Campo de Valor" };
    }
    return { x: "Eixo X", y: "Eixo Y" };
  };

  const labels = getFieldLabel();

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configurar Campos do Gráfico</DialogTitle>
          <DialogDescription>
            Selecione quais campos dos dados devem ser usados
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {availableFields.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum dado disponível para configurar.</p>
              <p className="text-sm mt-1">
                Adicione dados ao gráfico primeiro.
              </p>
            </div>
          ) : (
            <>
              {/* Campo X */}
              <div className="space-y-2">
                <Label htmlFor="xField">{labels.x}</Label>
                <Select value={xKey} onValueChange={setXKey}>
                  <SelectTrigger id="xField">
                    <SelectValue placeholder="Selecione um campo" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFields.map((field) => (
                      <SelectItem key={field} value={field}>
                        {field}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Campo Y */}
              <div className="space-y-2">
                <Label htmlFor="yField">{labels.y}</Label>
                <Select value={yKey} onValueChange={setYKey}>
                  <SelectTrigger id="yField">
                    <SelectValue placeholder="Selecione um campo" />
                  </SelectTrigger>
                  <SelectContent>
                    {numericFields.map((field) => (
                      <SelectItem key={field} value={field}>
                        {field}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {numericFields.length === 0 && (
                  <p className="text-xs text-amber-600">
                    Nenhum campo numérico encontrado nos dados
                  </p>
                )}
              </div>

              {/* Checkbox para agrupar duplicados */}
              <div className="mt-4 p-4 bg-accent border border-primary/20 rounded-md">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shouldGroupDuplicates}
                    onChange={(e) => setShouldGroupDuplicates(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium">
                      Agrupar valores duplicados
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Agrupa registros com o mesmo valor no {labels.x.toLowerCase()} e 
                      soma os valores do {labels.y.toLowerCase()}
                    </p>
                  </div>
                </label>
              </div>

              {/* Limites e Ordenação */}
              <div className="mt-4 p-4 bg-accent border border-primary/20 rounded-md space-y-3">
                <h4 className="text-sm font-medium">Filtros de Dados</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Limite de registros */}
                  <div className="space-y-2">
                    <Label htmlFor="dataLimit" className="text-sm">
                      Quantidade de registros
                    </Label>
                    <Input
                      id="dataLimit"
                      type="number"
                      min="1"
                      value={dataLimit || ""}
                      onChange={(e) => setDataLimit(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="Todos"
                    />
                    <p className="text-xs text-muted-foreground">
                      Deixe vazio para mostrar todos
                    </p>
                  </div>

                  {/* Ordenação */}
                  <div className="space-y-2">
                    <Label htmlFor="sortOrder" className="text-sm">
                      Ordenar por valor
                    </Label>
                    <Select
                      value={dataSortOrder}
                      onValueChange={(value: "asc" | "desc") => setDataSortOrder(value)}
                    >
                      <SelectTrigger id="sortOrder">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desc">Maiores valores</SelectItem>
                        <SelectItem value="asc">Menores valores</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Preview dos dados */}
              {xKey && yKey && previewData.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Prévia dos Dados:
                    {shouldGroupDuplicates && (
                      <span className="ml-2 text-xs text-green-600 font-normal">
                        (agrupados)
                      </span>
                    )}
                  </h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {previewData.slice(0, 5).map((item: any, idx: number) => (
                      <div
                        key={idx}
                        className="text-xs text-gray-600 flex justify-between"
                      >
                        <span className="font-medium">{item[xKey]}:</span>
                        <span>{item[yKey]}</span>
                      </div>
                    ))}
                    {previewData.length > 5 && (
                      <p className="text-xs text-gray-400 mt-1">
                        ... e mais {previewData.length - 5} registros
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!xKey || !yKey}
          >
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
